/*
 * AS cart "Share or request a quote".
 *
 * Reuses the product page's Share/Quote dialog behavior (layout/theme.liquid, PDP Change 073) on the cart:
 * - Share: one Cart Link for every item. Each item carries its _as_cfg code, which the product page saves
 *   on the cart line at Add to cart (the same code as the quote email's Configuration URL).
 * - Request a quote: the same Shopify contact-form email, with one block and one Configuration URL per item.
 * - Opening a Cart Link (/cart?as_cart=v1....) shows a "Shared with you" list above the cart.
 *
 * Cart Link format: /cart?as_cart=v1.<item>.<item>... where <item> is handle~variantId~quantity~code
 * (code is empty for plain products). Handles, variant IDs, quantities and base64url codes never contain
 * "." or "~", so the format splits unambiguously. The AS Order Builder reads the same format.
 */
(() => {
  if (window.asCartShareQuoteLoaded) return;
  window.asCartShareQuoteLoaded = true;

  const DIALOG_ID = 'AsCartShareQuote';
  const MAX_LINK_LENGTH = 8192; // AS Order Builder URL limit
  const MAX_CODE_LENGTH = 4096; // product-page as_cfg limit
  const MAX_LINK_ITEMS = 50;
  const CODE_PATTERN = /^[A-Za-z0-9_-]+$/;
  const HANDLE_PATTERN = /^[a-z0-9][a-z0-9-]*$/;
  const shareQuoteActions = new Set(['open', 'share', 'copy', 'quote_submit']);

  let currentModel = null;

  /* ---------- helpers ---------- */

  function el(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (typeof text !== 'undefined') node.textContent = text;
    return node;
  }

  function money(cents, currency) {
    const amount = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format(amount);
    } catch (error) {
      return `$${amount.toFixed(2)}`;
    }
  }

  function todayLabel() {
    return new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  function itemCountLabel(count) {
    return `${count} ${count === 1 ? 'item' : 'items'}`;
  }

  function decodeCode(code) {
    if (!code) return [];
    try {
      const normalized = code.replace(/-/g, '+').replace(/_/g, '/');
      const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
      const decoded = JSON.parse(decodeURIComponent(escape(atob(padded))));
      return Array.isArray(decoded)
        ? decoded.filter((pair) => Array.isArray(pair) && pair.length === 2 && typeof pair[0] === 'string')
        : [];
    } catch (error) {
      return [];
    }
  }

  // Same preview-theme handling as the product page, so QA on an unpublished theme returns to that theme.
  function previewParams() {
    const retained = {};
    try {
      const params = new URLSearchParams(location.search);
      const previewThemeId = params.get('preview_theme_id') || '';
      const previewBar = params.get('pb') || '';
      if (/^\d{1,20}$/.test(previewThemeId)) retained.preview_theme_id = previewThemeId;
      if (/^[01]$/.test(previewBar)) retained.pb = previewBar;
    } catch (error) {
      /* fall through to Shopify's theme identity */
    }
    if (!retained.preview_theme_id) {
      const theme = window.Shopify?.theme;
      const themeId = String(theme?.id || '');
      const themeRole = String(theme?.role || '').toLowerCase();
      if (/^\d{1,20}$/.test(themeId) && themeRole === 'unpublished') {
        retained.preview_theme_id = themeId;
        retained.pb = retained.pb || '0';
      }
    }
    if (!retained.preview_theme_id) delete retained.pb;
    return retained;
  }

  /* ---------- cart model ---------- */

  async function loadModel() {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(`cart.js ${response.status}`);
    currentModel = buildModel(await response.json());
    return currentModel;
  }

  function buildModel(cart) {
    const currency = cart.currency || 'USD';
    const lines = (cart.items || []).map((item) => {
      const properties = item.properties && typeof item.properties === 'object' ? item.properties : {};
      const rawCode = typeof properties._as_cfg === 'string' ? properties._as_cfg : '';
      const code = rawCode && rawCode.length <= MAX_CODE_LENGTH && CODE_PATTERN.test(rawCode) ? rawCode : '';
      const isGlobo = Object.keys(properties).some((key) => key === '_has_gpo' || key.startsWith('_gpo_'));
      const group = typeof properties._gpo_product_group === 'string' ? properties._gpo_product_group : '';
      const visible = Object.entries(properties).filter(([key, value]) => (
        !key.startsWith('_') && value !== null && typeof value !== 'undefined' && String(value).trim() !== ''
      ));
      const codeNames = new Set(decodeCode(code).map((pair) => pair[0]));
      // Visible fields the code cannot restore (typed text, measurements, uploads) must be re-entered by staff.
      const reenter = code ? visible.filter(([key]) => !codeNames.has(key)).map(([key]) => key) : [];
      const options = item.product_has_only_default_variant
        ? []
        : (item.options_with_values || []).filter((option) => option && option.name && option.value);
      const productUrl = `${location.origin}/products/${item.handle}?variant=${item.variant_id}`;
      return {
        item,
        code,
        isGlobo,
        group,
        visible,
        reenter,
        options,
        productUrl,
        configUrl: code ? `${productUrl}&as_cfg=${code}` : productUrl,
        components: [],
        isComponent: false,
      };
    });

    // Lines that Globo groups with a configured parent travel with that parent (its code restores them).
    const parents = new Map();
    lines.forEach((line) => {
      if (line.group && line.code && !parents.has(line.group)) parents.set(line.group, line);
    });
    lines.forEach((line) => {
      const parent = line.group ? parents.get(line.group) : null;
      if (parent && parent !== line) {
        line.isComponent = true;
        parent.components.push(line);
      }
    });

    const items = lines.filter((line) => !line.isComponent);
    const linkable = items.length > 0 && items.length <= MAX_LINK_ITEMS && items.every((line) => (
      HANDLE_PATTERN.test(String(line.item.handle || '')) && /^\d{1,20}$/.test(String(line.item.variant_id))
    ));
    let cartLink = '';
    if (linkable) {
      const parts = items.map((line) => [line.item.handle, line.item.variant_id, line.item.quantity, line.code].join('~'));
      const candidate = `${location.origin}/cart?as_cart=v1.${parts.join('.')}`;
      if (candidate.length <= MAX_LINK_LENGTH) cartLink = candidate;
    }
    return { cart, currency, items, cartLink };
  }

  function itemBlock(line, model) {
    const { item } = line;
    const rows = [item.product_title];
    rows.push(item.quantity > 1
      ? `Quantity: ${item.quantity} · ${money(item.final_price, model.currency)} each · ${money(item.final_line_price, model.currency)}`
      : `Quantity: 1 · ${money(item.final_line_price, model.currency)}`);
    rows.push(`SKU: ${item.sku || 'none'} · Variant ID: ${item.variant_id}`);
    line.options.forEach((option) => rows.push(`${option.name}: ${option.value}`));
    line.visible.forEach(([key, value]) => rows.push(`${key}: ${String(value)}`));
    line.components.forEach((component) => {
      const variant = component.item.variant_title ? ` (${component.item.variant_title})` : '';
      rows.push(`Includes: ${component.item.quantity} × ${component.item.product_title}${variant}`);
    });
    if (line.reenter.length) rows.push(`Re-enter from this email: ${line.reenter.join(', ')}`);
    return rows.join('\n').slice(0, 6000);
  }

  function itemLinkValue(line, index) {
    if (line.code || !line.isGlobo) return line.configUrl;
    return `Unavailable. Rebuild from the options listed in Item ${index + 1}. Product page: ${line.productUrl}`;
  }

  function discountSummary(model) {
    const totals = new Map();
    const add = (title, cents) => {
      if (!title) return;
      totals.set(title, (totals.get(title) || 0) + (Number(cents) || 0));
    };
    (model.cart.cart_level_discount_applications || []).forEach((discount) => {
      add(discount.title, discount.total_allocated_amount);
    });
    (model.cart.items || []).forEach((item) => {
      (item.line_level_discount_allocations || []).forEach((allocation) => {
        add(allocation.discount_application?.title, allocation.amount);
      });
    });
    return Array.from(totals.entries())
      .map(([title, cents]) => `${title} (−${money(cents, model.currency)})`)
      .join('; ');
  }

  function shareText(model) {
    const names = model.items.map((line) => `${line.item.quantity} × ${line.item.product_title}`).join('; ');
    const base = `Items I'm considering from Adaptive Specialties (${todayLabel()}): ${names}. ` +
      `Estimated subtotal ${money(model.cart.total_price, model.currency)}. Displayed prices are estimates only.`;
    return model.cartLink ? base : `${base}\n${model.items.map((line) => line.configUrl).join('\n')}`;
  }

  function shareLinkText(model) {
    return model.cartLink || model.items.map((line) => line.configUrl).join('\n');
  }

  /* ---------- dialog ---------- */

  function setStatus(dialog, message) {
    const status = dialog.querySelector('[data-as-share-status]');
    if (status) status.textContent = message;
  }

  function setMode(dialog, mode) {
    const selectedMode = mode === 'quote' ? 'quote' : 'share';
    dialog.querySelectorAll('[data-as-share-quote-mode]').forEach((button) => {
      button.setAttribute('aria-pressed', button.dataset.asShareQuoteMode === selectedMode ? 'true' : 'false');
    });
    dialog.querySelectorAll('[data-as-share-quote-panel]').forEach((panel) => {
      panel.hidden = panel.dataset.asShareQuotePanel !== selectedMode;
    });
  }

  function renderShareSummary(dialog, model) {
    const summary = dialog.querySelector('[data-as-share-summary]');
    if (summary) {
      summary.replaceChildren();
      const list = document.createElement('dl');
      const addRow = (label, value) => {
        const row = document.createElement('div');
        row.append(el('dt', '', label), el('dd', '', value));
        list.appendChild(row);
      };
      model.items.forEach((line) => {
        addRow(line.item.product_title, `Quantity ${line.item.quantity} · ${money(line.item.final_line_price, model.currency)}`);
      });
      addRow('Estimated subtotal', money(model.cart.total_price, model.currency));
      addRow('Prices as of', todayLabel());
      summary.appendChild(list);
    }
    const heading = dialog.querySelector('[data-as-cart-quote-heading]');
    if (heading) heading.textContent = `${itemCountLabel(model.items.length)} · ${money(model.cart.total_price, model.currency)}`;
    const fallback = dialog.querySelector('[data-as-share-fallback]');
    if (fallback) fallback.value = shareLinkText(model);
    if (!model.cartLink) {
      setStatus(dialog, 'This cart is too large for one link, so each item gets its own link.');
    }
  }

  function hiddenField(name, value) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    return input;
  }

  function populateQuote(dialog, model) {
    const form = dialog.querySelector('.as-share-quote__form');
    if (!form) return;
    const estimate = form.querySelector('[data-as-quote-field="estimate"]');
    if (estimate) estimate.value = `${money(model.cart.total_price, model.currency)} (cart subtotal)`;
    const discounts = form.querySelector('[data-as-quote-field="discounts"]');
    if (discounts) {
      const summary = discountSummary(model);
      discounts.value = summary;
      discounts.disabled = !summary;
    }
    const holder = form.querySelector('[data-as-quote-items]');
    if (holder) {
      holder.replaceChildren();
      model.items.forEach((line, index) => {
        holder.append(
          hiddenField(`contact[Item ${index + 1}]`, itemBlock(line, model)),
          hiddenField(`contact[Item ${index + 1} configuration URL]`, itemLinkValue(line, index))
        );
      });
    }
    const cartLink = form.querySelector('[data-as-quote-field="cart-link"]');
    if (cartLink) {
      cartLink.value = model.cartLink || 'Too large for one link. Use the item configuration URLs above.';
    }
    // The dialog header already shows the item count and subtotal; this line is kept for error messages only.
    const context = dialog.querySelector('[data-as-quote-context]');
    if (context) context.textContent = '';
    const returnField = form.querySelector('input[name="return_to"]');
    if (returnField) {
      const url = new URL('/cart', location.origin);
      Object.entries(previewParams()).forEach(([key, value]) => url.searchParams.set(key, value));
      url.searchParams.set('contact_posted', 'true');
      url.searchParams.set('as_quote', '1');
      returnField.value = `${url.pathname}${url.search}#${DIALOG_ID}`;
    }
    form.dataset.asQuoteReady = 'true';
  }

  function valueBand(cents) {
    const dollars = (Number(cents) || 0) / 100;
    if (dollars >= 2000) return '2000+';
    if (dollars >= 1000) return '1000-1999';
    if (dollars >= 500) return '500-999';
    return 'under-500';
  }

  function analyticsAllowed() {
    try {
      return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed() === true;
    } catch (error) {
      return false;
    }
  }

  // Same consent-gated pattern as the product page; no names, contact details, options, URLs or prices.
  function emit(action) {
    if (!shareQuoteActions.has(action) || !analyticsAllowed()) return;
    const payload = {
      action,
      source: 'cart',
      valueBand: currentModel ? valueBand(currentModel.cart.total_price) : 'unknown',
      itemCount: currentModel ? currentModel.items.length : 0,
    };
    try {
      document.dispatchEvent(new CustomEvent('on:as-cart-share-quote', { detail: { ...payload } }));
    } catch (error) {
      /* CustomEvent unavailable; continue */
    }
    const publish = window.Shopify?.analytics?.publish;
    if (typeof publish !== 'function') return;
    try {
      const result = publish.call(window.Shopify.analytics, 'adaptive_specialties:cart_share_quote', { ...payload });
      if (result && typeof result.catch === 'function') result.catch(() => {});
    } catch (error) {
      /* Analytics must never interrupt sharing or quote submission. */
    }
  }

  async function openDialog(mode, trigger) {
    const dialog = document.getElementById(DIALOG_ID);
    if (!dialog) return false;
    dialog.asShareQuoteTrigger = trigger || dialog.asShareQuoteTrigger;
    setMode(dialog, mode);
    setStatus(dialog, '');
    if (typeof dialog.showModal === 'function') {
      if (!dialog.open) dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
    dialog.querySelector(`[data-as-share-quote-mode="${mode === 'quote' ? 'quote' : 'share'}"]`)?.focus();

    const submit = dialog.querySelector('[data-as-quote-submit]');
    if (submit) submit.disabled = true;
    try {
      const model = await loadModel();
      renderShareSummary(dialog, model);
      populateQuote(dialog, model);
      if (submit) submit.disabled = false;
      return true;
    } catch (error) {
      const message = 'We could not read your cart. Please refresh the page and try again.';
      setStatus(dialog, message);
      const context = dialog.querySelector('[data-as-quote-context]');
      if (context) context.textContent = message;
      return false;
    }
  }

  function closeDialog(dialog) {
    if (typeof dialog.close === 'function' && dialog.open) dialog.close();
    else dialog.removeAttribute('open');
  }

  function revealFallback(dialog, message) {
    const wrap = dialog.querySelector('[data-as-share-fallback-wrap]');
    const fallback = dialog.querySelector('[data-as-share-fallback]');
    if (wrap) wrap.hidden = false;
    if (fallback) {
      fallback.focus();
      fallback.select();
    }
    setStatus(dialog, message || 'Select the text and copy it.');
  }

  async function copyLink(dialog) {
    if (!currentModel) return false;
    const text = shareLinkText(currentModel);
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      try {
        await navigator.clipboard.writeText(text);
        setStatus(dialog, currentModel.cartLink ? 'Cart link copied.' : 'Item links copied.');
        return true;
      } catch (error) {
        /* show the selectable fallback below */
      }
    }
    revealFallback(dialog, 'Automatic copy is unavailable. Select and copy the cart link.');
    return false;
  }

  async function shareCart(dialog) {
    if (!currentModel) return;
    if (typeof navigator.share === 'function') {
      try {
        const data = { title: 'Adaptive Specialties cart', text: shareText(currentModel) };
        if (currentModel.cartLink) data.url = currentModel.cartLink;
        await navigator.share(data);
        setStatus(dialog, 'Cart shared.');
        return;
      } catch (error) {
        if (error?.name === 'AbortError') {
          setStatus(dialog, 'Sharing canceled.');
          return;
        }
      }
    }
    const copied = await copyLink(dialog);
    if (copied) setStatus(dialog, 'Could not open sharing. Cart link copied instead.');
  }

  function setupDialog() {
    const dialog = document.getElementById(DIALOG_ID);
    if (!dialog || dialog.dataset.asCartShareQuoteReady === 'true') return;
    dialog.dataset.asCartShareQuoteReady = 'true';

    dialog.querySelectorAll('[data-as-share-quote-close]').forEach((button) => {
      button.addEventListener('click', () => closeDialog(dialog));
    });
    dialog.querySelectorAll('[data-as-share-quote-mode]').forEach((button) => {
      button.addEventListener('click', () => setMode(dialog, button.dataset.asShareQuoteMode));
    });
    dialog.querySelector('[data-as-share-primary]')?.addEventListener('click', () => {
      emit('share');
      shareCart(dialog);
    });
    dialog.querySelector('[data-as-share-copy]')?.addEventListener('click', () => {
      emit('copy');
      copyLink(dialog);
    });
    const form = dialog.querySelector('.as-share-quote__form');
    form?.addEventListener('submit', (event) => {
      if (form.dataset.asQuoteReady !== 'true') {
        event.preventDefault();
        const context = dialog.querySelector('[data-as-quote-context]');
        if (context) context.textContent = 'Your cart is still loading. Please try again in a moment.';
        return;
      }
      emit('quote_submit');
    });
    // Close on the backdrop only when the press and the release both land outside the dialog box,
    // so a drag that starts in a field (text selection, trackpad movement) never closes it.
    let pressedOutside = false;
    const outsideDialog = (event) => {
      if (event.target !== dialog) return false;
      const box = dialog.getBoundingClientRect();
      return event.clientX < box.left || event.clientX > box.right ||
        event.clientY < box.top || event.clientY > box.bottom;
    };
    dialog.addEventListener('pointerdown', (event) => {
      pressedOutside = outsideDialog(event);
    });
    dialog.addEventListener('click', (event) => {
      if (pressedOutside && outsideDialog(event)) closeDialog(dialog);
      pressedOutside = false;
    });
    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !dialog.showModal) {
        event.preventDefault();
        closeDialog(dialog);
        return;
      }
      if (event.key !== 'Tab' || !dialog.open) return;
      const focusable = Array.from(dialog.querySelectorAll(
        'button:not([disabled]), input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), select:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
      )).filter((node) => node.offsetParent !== null || node.getClientRects().length > 0);
      if (!focusable.length) {
        event.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
    dialog.addEventListener('close', () => {
      const returnFocus = dialog.asShareQuoteTrigger;
      if (returnFocus?.isConnected) {
        returnFocus.focus();
      } else {
        document.querySelector('.as-cart-share-quote [data-as-share-quote-open]')?.focus();
      }
    });

    // After the contact form posts, Shopify reloads /cart?contact_posted=true&as_quote=1.
    // Success: confirm with a brief message instead of reopening the dialog. Errors: reopen to fix them.
    const quoteResponse = dialog.querySelector('[data-as-quote-response]');
    if (quoteResponse?.dataset.asQuoteResponse === 'success') {
      quoteResponse.remove();
      showSentToast();
      clearQuoteParams();
    } else if (quoteResponse) {
      openDialog('quote');
    }
  }

  function showSentToast() {
    const toast = el('div', 'as-share-quote-toast');
    toast.setAttribute('role', 'status');
    const close = el('button', 'as-share-quote-toast__close', '\u00d7');
    close.type = 'button';
    close.setAttribute('aria-label', 'Dismiss');
    close.addEventListener('click', () => toast.remove());
    toast.append(el('p', '', 'Thank you. Your quote request was sent, and our team will be in touch.'), close);
    document.body.appendChild(toast);
    window.setTimeout(() => toast.remove(), 10000);
  }

  function clearQuoteParams() {
    try {
      const url = new URL(location.href);
      url.searchParams.delete('contact_posted');
      url.searchParams.delete('as_quote');
      history.replaceState(history.state, '', `${url.pathname}${url.search}`);
    } catch (error) {
      /* leave the address as it is */
    }
  }

  // The order summary is redrawn after every cart change, so the trigger is handled at page level.
  document.addEventListener('click', (event) => {
    const trigger = event.target instanceof Element
      ? event.target.closest('.as-cart-share-quote [data-as-share-quote-open]')
      : null;
    if (!trigger) return;
    event.preventDefault();
    openDialog('share', trigger).then((loaded) => {
      if (loaded) emit('open');
    });
  });

  /* ---------- "Shared with you" (opening a Cart Link) ---------- */

  // The list stays on the cart page for the rest of the visit (this browser tab), so a recipient can
  // add an item from its product page, come back through "View cart", and continue.
  const SHARED_KEY = 'asSharedCart';

  function storedSharedParam() {
    try {
      return sessionStorage.getItem(SHARED_KEY);
    } catch (error) {
      return null;
    }
  }

  function storeSharedParam(value) {
    try {
      if (value) sessionStorage.setItem(SHARED_KEY, value);
      else sessionStorage.removeItem(SHARED_KEY);
    } catch (error) {
      /* storage unavailable: the list shows only when the link itself is opened */
    }
  }

  function parseCartParam(value) {
    if (typeof value !== 'string' || !value.startsWith('v1.') || value.length > MAX_LINK_LENGTH) return null;
    const parts = value.slice(3).split('.');
    if (!parts.length || parts.length > MAX_LINK_ITEMS) return null;
    const entries = [];
    for (const part of parts) {
      const [handle = '', variantId = '', quantity = '', code = ''] = part.split('~');
      const qty = Number(quantity);
      if (!HANDLE_PATTERN.test(handle) || !/^\d{1,20}$/.test(variantId)) return null;
      if (!Number.isInteger(qty) || qty < 1 || qty > 999) return null;
      if (code && (code.length > MAX_CODE_LENGTH || !CODE_PATTERN.test(code))) return null;
      entries.push({ handle, variantId, quantity: qty, code });
    }
    return entries;
  }

  function imageUrl(src) {
    if (!src || typeof src !== 'string') return '';
    const absolute = src.startsWith('//') ? `https:${src}` : src;
    return `${absolute}${absolute.includes('?') ? '&' : '?'}width=128`;
  }

  function titleFromHandle(handle) {
    return handle.split('-').filter(Boolean).map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  }

  // Lines already in this visitor's cart, keyed by variant and options code.
  async function readCartLines() {
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return null;
      const cart = await response.json();
      const lines = new Map();
      (cart.items || []).forEach((item) => {
        const code = typeof item.properties?._as_cfg === 'string' ? item.properties._as_cfg : '';
        const key = `${item.variant_id}~${code}`;
        if (!lines.has(key)) lines.set(key, { lineKey: item.key, quantity: item.quantity });
      });
      return lines;
    } catch (error) {
      return null;
    }
  }

  async function updateQuantity(lineKey, quantity, button) {
    button.disabled = true;
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity }),
      });
      if (!response.ok) throw new Error(`cart/change.js ${response.status}`);
      location.reload();
    } catch (error) {
      button.disabled = false;
      button.textContent = 'Try again';
    }
  }

  async function sharedItemRow(entry, line) {
    const row = el('li', 'as-shared-cart__item');
    let product = null;
    let missing = false;
    try {
      const response = await fetch(`/products/${entry.handle}.js`, { headers: { Accept: 'application/json' } });
      if (response.ok) product = await response.json();
      else if (response.status === 404) missing = true;
    } catch (error) {
      product = null; // network or rate-limit problem: still offer the item's link below
    }
    const variant = product?.variants?.find((candidate) => String(candidate.id) === entry.variantId) || null;

    const media = el('div', 'as-shared-cart__media');
    const src = imageUrl(variant?.featured_image?.src || product?.featured_image || '');
    if (src) {
      const img = document.createElement('img');
      img.src = src;
      img.alt = '';
      img.width = 64;
      img.height = 64;
      img.loading = 'lazy';
      media.appendChild(img);
    }

    const body = el('div', 'as-shared-cart__body');
    body.appendChild(el('p', 'as-shared-cart__title', product?.title || (missing ? 'This product is no longer available' : titleFromHandle(entry.handle))));
    const meta = [];
    if (variant?.title && variant.title !== 'Default Title') meta.push(variant.title);
    meta.push(`Quantity ${entry.quantity}`);
    body.appendChild(el('p', 'as-shared-cart__meta', meta.join(' · ')));

    if (line && line.quantity >= entry.quantity) {
      row.append(media, body, el('span', 'as-shared-cart__in-cart', 'In your cart'));
      return row;
    }
    if (line) {
      body.appendChild(el('p', 'as-shared-cart__status', `${line.quantity} in your cart`));
      const update = el('button', 'btn as-share-quote__trigger', `Update to ${entry.quantity}`);
      update.type = 'button';
      update.addEventListener('click', () => updateQuantity(line.lineKey, entry.quantity, update));
      row.append(media, body, update);
      return row;
    }

    const action = el('a', 'btn as-share-quote__trigger');
    if (missing) {
      action.href = `/search?q=${encodeURIComponent(titleFromHandle(entry.handle))}`;
      action.textContent = 'Search the store';
    } else if (product && !variant) {
      body.appendChild(el('p', 'as-shared-cart__note', 'This option is no longer available. Open the product to choose again.'));
      action.href = `/products/${entry.handle}`;
      action.textContent = 'View product';
    } else {
      if (entry.quantity > 1) {
        body.appendChild(el('p', 'as-shared-cart__note', `After adding it, you can update the quantity to ${entry.quantity} here.`));
      }
      action.href = `/products/${entry.handle}?variant=${entry.variantId}${entry.code ? `&as_cfg=${entry.code}` : ''}`;
      action.textContent = entry.code ? 'Open with these options' : 'View product';
    }
    row.append(media, body, action);
    return row;
  }

  async function renderSharedCart() {
    const host = document.querySelector('[data-as-shared-cart]');
    if (!host) return;
    let param = null;
    try {
      param = new URLSearchParams(location.search).get('as_cart');
    } catch (error) {
      param = null;
    }
    if (param) storeSharedParam(param);
    else param = storedSharedParam();
    if (!param) return;

    const intro = el('p', 'as-shared-cart__intro', 'Open each item to review its selected options, then add it to your cart.');
    const hide = el('button', 'as-shared-cart__hide', 'Hide');
    hide.type = 'button';
    hide.addEventListener('click', () => {
      storeSharedParam('');
      host.hidden = true;
    });
    const header = el('div', 'as-shared-cart__header');
    header.append(el('h2', 'h5', 'Shared with you'), hide);
    host.replaceChildren(header, intro);
    host.hidden = false;

    const entries = parseCartParam(param);
    if (!entries) {
      intro.textContent = 'This shared cart link could not be read. Please ask the sender to share it again.';
      storeSharedParam('');
      return;
    }
    const lines = await readCartLines();
    const lineFor = (entry) => lines?.get(`${entry.variantId}~${entry.code}`) || null;
    if (lines && entries.every((entry) => (lineFor(entry)?.quantity || 0) >= entry.quantity)) {
      intro.textContent = 'Everything in this shared cart is in your cart below.';
      storeSharedParam('');
      return;
    }
    const list = el('ul', 'as-shared-cart__list');
    host.appendChild(list);
    for (const entry of entries) {
      list.appendChild(await sharedItemRow(entry, lineFor(entry)));
    }
  }

  function init() {
    setupDialog();
    renderSharedCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
